// Helpers for the MmdlViewer sketch layer (CR #207): dimensional-lumber sizes
// and feet-inches formatting/parsing. The .mmdl models are in FEET (verified
// empirically on job 13's model: truss chord thickness parses as 0.13 ft =
// 1-1/2", building footprint ~93 ft × 72 ft).

export type LumberSizeKey = '2x4' | '2x6' | '2x8' | '2x10' | '2x12' | '4x4' | '6x6';

// True (dressed) cross-sections in inches: thick × depth.
export const LUMBER_SIZES: { key: LumberSizeKey; thick: number; depth: number }[] = [
  { key: '2x4', thick: 1.5, depth: 3.5 },
  { key: '2x6', thick: 1.5, depth: 5.5 },
  { key: '2x8', thick: 1.5, depth: 7.25 },
  { key: '2x10', thick: 1.5, depth: 9.25 },
  { key: '2x12', thick: 1.5, depth: 11.25 },
  { key: '4x4', thick: 3.5, depth: 3.5 },
  { key: '6x6', thick: 5.5, depth: 5.5 },
];

export function lumberDims(size: LumberSizeKey): { thick: number; depth: number } {
  return LUMBER_SIZES.find(s => s.key === size) ?? LUMBER_SIZES[0];
}

// Plywood true thicknesses (inches) — label → value.
export const PLY_THICKNESSES: { label: string; thick: number }[] = [
  { label: '3/8"', thick: 0.375 },
  { label: '1/2"', thick: 0.5 },
  { label: '5/8"', thick: 0.625 },
  { label: '3/4"', thick: 0.75 },
];

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** Format a length given in inches as feet-inches to the nearest 1/16" (e.g. 16' 3-5/16"). */
export function formatInches(totalInches: number): string {
  let sixteenths = Math.round(Math.abs(totalInches) * 16);
  const whole = Math.floor(sixteenths / 16);
  sixteenths -= whole * 16;
  let frac = '';
  if (sixteenths > 0) {
    const g = gcd(sixteenths, 16);
    frac = `${sixteenths / g}/${16 / g}`;
  }
  const ft = Math.floor(whole / 12);
  const inches = whole % 12;
  const inchStr = frac ? (inches > 0 ? `${inches}-${frac}"` : `${frac}"`) : `${inches}"`;
  if (ft === 0) return inchStr;
  return `${ft}' ${inchStr}`;
}

/** Format a length given in feet (model units) as feet-inches to the nearest 1/16". */
export function formatFeet(feet: number): string {
  return formatInches(feet * 12);
}

/**
 * Parse a carpenter-style length into inches. Accepts: 10 · 10' · 10' 6 ·
 * 10' 6" · 10-6 · 10 6 · 6" · 6-1/2" · 3/4". A bare number means feet.
 * Returns null when unreadable.
 */
export function parseFeetInches(input: string): number | null {
  const s = input.trim().toLowerCase().replace(/ft\b/g, "'").replace(/in\b/g, '"').replace(/\s+/g, ' ');
  if (!s) return null;

  const parseInchToken = (t: string): number | null => {
    // 6 | 6.5 | 6-1/2 | 6 1/2 | 1/2
    let m = t.match(/^(\d+(?:\.\d+)?)(?:[- ](\d+)\/(\d+))?$/);
    if (m) {
      const base = parseFloat(m[1]);
      if (m[2] && m[3]) {
        const den = parseInt(m[3], 10);
        if (den === 0) return null;
        return base + parseInt(m[2], 10) / den;
      }
      return base;
    }
    m = t.match(/^(\d+)\/(\d+)$/);
    if (m) {
      const den = parseInt(m[2], 10);
      if (den === 0) return null;
      return parseInt(m[1], 10) / den;
    }
    return null;
  };

  // Inches only: ends with " and has no '
  if (!s.includes("'") && s.endsWith('"')) {
    const v = parseInchToken(s.slice(0, -1).trim());
    return v === null ? null : v;
  }

  // Feet ' inches
  if (s.includes("'")) {
    const [ftPart, rest] = s.split("'", 2);
    const ft = parseFloat(ftPart.trim());
    if (!isFinite(ft)) return null;
    const restTrim = rest.replace(/"/g, '').trim();
    if (!restTrim) return ft * 12;
    const inches = parseInchToken(restTrim);
    if (inches === null) return null;
    return ft * 12 + inches;
  }

  // 10-6 or "10 6" → feet-inches
  const m = s.match(/^(\d+)[- ](\d+(?:[- ]\d+\/\d+)?)$/);
  if (m) {
    const inches = parseInchToken(m[2]);
    if (inches === null) return null;
    return parseInt(m[1], 10) * 12 + inches;
  }

  // Bare number → feet
  const n = parseFloat(s);
  if (isFinite(n)) return n * 12;
  return null;
}

/** Signed variant of parseFeetInches for coordinates (e.g. -3' 6"). Inches. */
export function parseSignedFeetInches(input: string): number | null {
  const t = input.trim();
  const neg = t.startsWith('-') || t.startsWith('−');
  const v = parseFeetInches(neg ? t.slice(1) : t);
  return v === null ? null : neg ? -v : v;
}

/** Format a signed length in feet as feet-inches (e.g. -3' 6"). */
export function formatSignedFeet(feet: number): string {
  const s = formatFeet(Math.abs(feet));
  return feet < -1 / 384 ? `-${s}` : s; // below −1/32" reads as negative
}

// ---------------------------------------------------------------------------
// Plywood cut-out outlines (CR #207 addition). A sheet is a rectangle
// [0,L]×[0,W] (inches) minus a list of axis-aligned cut rectangles. We trace
// the remaining region's contours on the coordinate grid — this uniformly
// handles interior holes, edge notches, corner notches, overlapping cuts and
// even cuts that split the sheet into separate islands. Output feeds
// THREE.Shape (outer ring) + Shape.holes per island; no CSG library needed.

export interface RectCut { x: number; y: number; w: number; h: number }
export type Pt = [number, number];
export interface SheetOutline { outer: Pt[]; holes: Pt[][] }

/** Angled end of a lumber member: mitre (swing across the width) and bevel
 *  (tilt through the thickness), in degrees. 0/0 = square end. */
export interface EndCut { m: number; b: number }

export const isSquare = (e?: EndCut): boolean => !e || (Math.abs(e.m) < 1e-6 && Math.abs(e.b) < 1e-6);

/**
 * Triangle soup (flat xyz positions) for a rectangular-section member with
 * optionally angled ends — a convex 8-vertex solid, no CSG needed. Local
 * frame: length along X, depth (width of the board) along Y, thickness along
 * Z. `Lft` is the LONG-POINT length (carpenter convention): the outermost
 * corner of each angled end lands exactly at ±L/2.
 */
export function angledLumberTriangles(Lft: number, Dft: number, Tft: number, e0?: EndCut, e1?: EndCut): number[] {
  // Corner loop CCW viewed from +X
  const corners: Pt[] = [[-Dft / 2, -Tft / 2], [Dft / 2, -Tft / 2], [Dft / 2, Tft / 2], [-Dft / 2, Tft / 2]];
  const offsets = (e?: EndCut): number[] => {
    const M = ((e?.m ?? 0) * Math.PI) / 180, B = ((e?.b ?? 0) * Math.PI) / 180;
    const nx = Math.cos(M) * Math.cos(B), ny = Math.sin(M) * Math.cos(B), nz = Math.sin(B);
    return corners.map(([y, z]) => -(ny * y + nz * z) / nx);
  };
  const o0 = offsets(e0), o1 = offsets(e1);
  const xA = -Lft / 2 - Math.min(...o0); // left plane centre so the long point sits at −L/2
  const xB = Lft / 2 - Math.max(...o1);  // right plane centre so the long point sits at +L/2
  const l = corners.map(([y, z], i) => [xA + o0[i], y, z]);
  const r = corners.map(([y, z], i) => [xB + o1[i], y, z]);
  const out: number[] = [];
  const tri = (a: number[], b: number[], c: number[]) => out.push(...a, ...b, ...c);
  tri(r[0], r[1], r[2]); tri(r[0], r[2], r[3]);       // right end (+X-ish)
  tri(l[0], l[3], l[2]); tri(l[0], l[2], l[1]);       // left end (−X-ish)
  for (let k = 0; k < 4; k++) {                        // four sides
    const k2 = (k + 1) % 4;
    tri(l[k], l[k2], r[k2]); tri(l[k], r[k2], r[k]);
  }
  return out;
}

/** Offset (inches, along the length axis) from an angled end's plane centre to
 *  its long point — used to convert a mid-axis cut position into the two
 *  pieces' long-point lengths. Returns { maxO, minO } in the same units as D/T. */
export function endOffsets(Dft: number, Tft: number, e?: EndCut): { maxO: number; minO: number } {
  const M = ((e?.m ?? 0) * Math.PI) / 180, B = ((e?.b ?? 0) * Math.PI) / 180;
  const nx = Math.cos(M) * Math.cos(B), ny = Math.sin(M) * Math.cos(B), nz = Math.sin(B);
  const corners: Pt[] = [[-Dft / 2, -Tft / 2], [Dft / 2, -Tft / 2], [Dft / 2, Tft / 2], [-Dft / 2, Tft / 2]];
  const offs = corners.map(([y, z]) => -(ny * y + nz * z) / nx);
  return { maxO: Math.max(...offs), minO: Math.min(...offs) };
}

export function polySignedArea(ring: Pt[]): number {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i], [x2, y2] = ring[(i + 1) % ring.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

export function pointInPoly(p: Pt, ring: Pt[]): boolean {
  let inn = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inn = !inn;
  }
  return inn;
}

export function polyBounds(poly: Pt[]): { x: number; y: number; w: number; h: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of poly) {
    if (x < x0) x0 = x; if (y < y0) y0 = y;
    if (x > x1) x1 = x; if (y > y1) y1 = y;
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** True when poly is (within tolerance) exactly the rectangle [0,L]×[0,W]. */
export function polyIsRect(poly: Pt[], L: number, W: number, eps = 1 / 32): boolean {
  if (poly.length !== 4) return false;
  return poly.every(([x, y]) =>
    (Math.abs(x) < eps || Math.abs(x - L) < eps) && (Math.abs(y) < eps || Math.abs(y - W) < eps));
}

/**
 * Clip a polygon to one side of the infinite line through a→b
 * (side=+1 keeps the left of a→b, side=−1 the right). Standard half-plane
 * Sutherland–Hodgman — exact for convex outlines; concave outlines crossed
 * more than twice get bridged (documented limitation).
 */
export function clipPolyHalfPlane(poly: Pt[], a: Pt, b: Pt, side: 1 | -1): Pt[] {
  const eps = 1e-6;
  const f = (p: Pt) => side * ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]));
  const out: Pt[] = [];
  for (let i = 0; i < poly.length; i++) {
    const cur = poly[i], nxt = poly[(i + 1) % poly.length];
    const fc = f(cur), fn = f(nxt);
    if (fc >= -eps) out.push(cur);
    if ((fc > eps && fn < -eps) || (fc < -eps && fn > eps)) {
      const t = fc / (fc - fn);
      out.push([cur[0] + t * (nxt[0] - cur[0]), cur[1] + t * (nxt[1] - cur[1])]);
    }
  }
  // Drop duplicate/collinear-degenerate points
  const clean: Pt[] = [];
  for (const p of out) {
    const prev = clean[clean.length - 1];
    if (!prev || Math.hypot(p[0] - prev[0], p[1] - prev[1]) > 1e-4) clean.push(p);
  }
  if (clean.length > 1) {
    const first = clean[0], last = clean[clean.length - 1];
    if (Math.hypot(first[0] - last[0], first[1] - last[1]) < 1e-4) clean.pop();
  }
  return clean;
}

/** Clamp a cut rect to the sheet bounds; null when nothing remains. */
export function clampCut(c: RectCut, L: number, W: number): RectCut | null {
  const x0 = Math.max(0, c.x), y0 = Math.max(0, c.y);
  const x1 = Math.min(L, c.x + c.w), y1 = Math.min(W, c.y + c.h);
  if (x1 - x0 < 1e-6 || y1 - y0 < 1e-6) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export function sheetOutlines(L: number, W: number, cuts: RectCut[]): SheetOutline[] {
  const rects = cuts.map(c => clampCut(c, L, W)).filter((c): c is RectCut => c !== null);
  if (rects.length === 0) return [{ outer: [[0, 0], [L, 0], [L, W], [0, W]], holes: [] }];

  const uniq = (vals: number[]) => {
    const s = [...vals].sort((a, b) => a - b);
    const out: number[] = [];
    for (const v of s) if (out.length === 0 || v - out[out.length - 1] > 1e-6) out.push(v);
    return out;
  };
  const xs = uniq([0, L, ...rects.flatMap(r => [r.x, r.x + r.w])]);
  const ys = uniq([0, W, ...rects.flatMap(r => [r.y, r.y + r.h])]);
  const nx = xs.length - 1, ny = ys.length - 1;

  // keep[i][j]: is the cell (xs[i]..xs[i+1], ys[j]..ys[j+1]) part of the sheet?
  const keep: boolean[][] = [];
  for (let i = 0; i < nx; i++) {
    keep.push([]);
    for (let j = 0; j < ny; j++) {
      const cx = (xs[i] + xs[i + 1]) / 2, cy = (ys[j] + ys[j + 1]) / 2;
      keep[i][j] = !rects.some(r => cx > r.x && cx < r.x + r.w && cy > r.y && cy < r.y + r.h);
    }
  }
  const kept = (i: number, j: number) => i >= 0 && j >= 0 && i < nx && j < ny && keep[i][j];

  // Boundary segments oriented with the interior on the LEFT of travel:
  // outer rings come out CCW, hole rings CW.
  type Seg = { a: Pt; b: Pt; used: boolean };
  const segs: Seg[] = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      if (!keep[i][j]) continue;
      if (!kept(i, j - 1)) segs.push({ a: [xs[i], ys[j]], b: [xs[i + 1], ys[j]], used: false });         // bottom → +x
      if (!kept(i, j + 1)) segs.push({ a: [xs[i + 1], ys[j + 1]], b: [xs[i], ys[j + 1]], used: false }); // top → −x
      if (!kept(i - 1, j)) segs.push({ a: [xs[i], ys[j + 1]], b: [xs[i], ys[j]], used: false });         // left → −y
      if (!kept(i + 1, j)) segs.push({ a: [xs[i + 1], ys[j]], b: [xs[i + 1], ys[j + 1]], used: false }); // right → +y
    }
  }

  const key = (p: Pt) => `${Math.round(p[0] * 1024)},${Math.round(p[1] * 1024)}`;
  const byStart = new Map<string, Seg[]>();
  for (const s of segs) {
    const k = key(s.a);
    const list = byStart.get(k);
    if (list) list.push(s); else byStart.set(k, [s]);
  }

  const loops: Pt[][] = [];
  for (const start of segs) {
    if (start.used) continue;
    const loop: Pt[] = [];
    let cur = start;
    for (let guard = 0; guard < segs.length + 1; guard++) {
      cur.used = true;
      loop.push(cur.a);
      const nexts = (byStart.get(key(cur.b)) ?? []).filter(s => !s.used);
      if (nexts.length === 0) break;
      // At degenerate (diagonal-touch) corners two segments start here; prefer
      // the sharpest RIGHT turn to keep separate regions from merging.
      const dir: Pt = [Math.sign(cur.b[0] - cur.a[0]), Math.sign(cur.b[1] - cur.a[1])];
      const right: Pt = [dir[1], -dir[0]];
      nexts.sort((s1, s2) => {
        const d1: Pt = [Math.sign(s1.b[0] - s1.a[0]), Math.sign(s1.b[1] - s1.a[1])];
        const d2: Pt = [Math.sign(s2.b[0] - s2.a[0]), Math.sign(s2.b[1] - s2.a[1])];
        const score = (d: Pt) => d[0] * right[0] + d[1] * right[1] - (d[0] * dir[0] + d[1] * dir[1]) * 0.5;
        return score(d2) - score(d1);
      });
      cur = nexts[0];
      if (cur.a[0] === start.a[0] && cur.a[1] === start.a[1] && key(cur.a) === key(start.a)) break;
    }
    // Drop collinear midpoints
    const clean: Pt[] = [];
    for (let i = 0; i < loop.length; i++) {
      const p0 = loop[(i + loop.length - 1) % loop.length], p1 = loop[i], p2 = loop[(i + 1) % loop.length];
      const collinear = (p1[0] - p0[0]) * (p2[1] - p0[1]) - (p1[1] - p0[1]) * (p2[0] - p0[0]);
      if (Math.abs(collinear) > 1e-6) clean.push(p1);
    }
    if (clean.length >= 3) loops.push(clean);
  }

  const signedArea = (ring: Pt[]) => {
    let a = 0;
    for (let i = 0; i < ring.length; i++) {
      const [x1, y1] = ring[i], [x2, y2] = ring[(i + 1) % ring.length];
      a += x1 * y2 - x2 * y1;
    }
    return a / 2;
  };
  const inside = (p: Pt, ring: Pt[]) => {
    let inn = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i], [xj, yj] = ring[j];
      if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inn = !inn;
    }
    return inn;
  };

  const outers = loops.filter(l => signedArea(l) > 0);
  const holes = loops.filter(l => signedArea(l) < 0);
  const result: SheetOutline[] = outers.map(outer => ({ outer, holes: [] }));
  for (const h of holes) {
    // A hole vertex lies ON the outer ring's grid; test a point nudged inside.
    const cx = h.reduce((s, p) => s + p[0], 0) / h.length;
    const cy = h.reduce((s, p) => s + p[1], 0) / h.length;
    const owner = result.find(r => inside([cx, cy], r.outer)) ?? result[0];
    if (owner) owner.holes.push(h);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Wave 4 (CR #207): "Autocut collisions" — convex-solid collision math with
// no CSG dependency. Every sketch piece is (or is wrapped as) a convex solid:
// lumber is an 8-vertex prism with optionally slanted ends; a plywood sheet is
// its outline extruded through the thickness, taken as the CONVEX HULL of the
// outline for detection (holes/notches are ignored while detecting — fine per
// spec). SAT answers "do they collide"; clipping each solid's faces against
// the other's planes recovers the intersection region's points, which drive
// crosscut stations (lumber) or a bounding cut-out rectangle (plywood).

export type Vec3 = [number, number, number];
export interface ConvexSolid { verts: Vec3[]; faces: number[][] }
export interface SolidPlane { n: Vec3; d: number } // inside: n·x ≤ d

const v3sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const v3cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const v3dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const v3norm = (a: Vec3): Vec3 | null => {
  const l = Math.hypot(a[0], a[1], a[2]);
  return l < 1e-9 ? null : [a[0] / l, a[1] / l, a[2] / l];
};

/**
 * Local-frame convex solid for a lumber member (same frame and long-point
 * convention as angledLumberTriangles: length along X, long points at ±L/2).
 * All dims in FEET.
 */
export function lumberSolid(Lft: number, Dft: number, Tft: number, e0?: EndCut, e1?: EndCut): ConvexSolid {
  const corners: Pt[] = [[-Dft / 2, -Tft / 2], [Dft / 2, -Tft / 2], [Dft / 2, Tft / 2], [-Dft / 2, Tft / 2]];
  const offsets = (e?: EndCut): number[] => {
    const M = ((e?.m ?? 0) * Math.PI) / 180, B = ((e?.b ?? 0) * Math.PI) / 180;
    const nx = Math.cos(M) * Math.cos(B), ny = Math.sin(M) * Math.cos(B), nz = Math.sin(B);
    return corners.map(([y, z]) => -(ny * y + nz * z) / nx);
  };
  const o0 = offsets(e0), o1 = offsets(e1);
  const xA = -Lft / 2 - Math.min(...o0);
  const xB = Lft / 2 - Math.max(...o1);
  const verts: Vec3[] = [
    ...corners.map(([y, z], i) => [xA + o0[i], y, z] as Vec3),
    ...corners.map(([y, z], i) => [xB + o1[i], y, z] as Vec3),
  ];
  const faces: number[][] = [[0, 3, 2, 1], [4, 5, 6, 7]];
  for (let k = 0; k < 4; k++) { const k2 = (k + 1) % 4; faces.push([k, k2, 4 + k2, 4 + k]); }
  return { verts, faces };
}

/** Monotone-chain 2D convex hull (returns CCW). */
export function convexHull2D(pts: Pt[]): Pt[] {
  const s = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (s.length <= 2) return s;
  const cross2 = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Pt[] = [];
  for (const p of s) {
    while (lower.length >= 2 && cross2(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = s.length - 1; i >= 0; i--) {
    const p = s[i];
    while (upper.length >= 2 && cross2(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}

/**
 * Local-frame convex solid for a sheet (mesh-local coords in FEET: length→X,
 * width→−Z, thickness→Y — matches the extruded/rotated geometry). Dims in
 * INCHES; an angle-cut outline is reduced to its convex hull.
 */
export function sheetSolid(LIn: number, WIn: number, TIn: number, poly?: Pt[]): ConvexSolid {
  const outline: Pt[] = poly && poly.length >= 3 ? convexHull2D(poly) : [[0, 0], [LIn, 0], [LIn, WIn], [0, WIn]];
  const n = outline.length;
  const toLocal = ([u, v]: Pt, top: boolean): Vec3 =>
    [u / 12 - LIn / 24, (top ? 1 : -1) * (TIn / 24), -(v / 12 - WIn / 24)];
  const verts: Vec3[] = [...outline.map(p => toLocal(p, false)), ...outline.map(p => toLocal(p, true))];
  const faces: number[][] = [
    Array.from({ length: n }, (_, i) => i),
    Array.from({ length: n }, (_, i) => n + i),
  ];
  for (let k = 0; k < n; k++) { const k2 = (k + 1) % n; faces.push([k, k2, n + k2, n + k]); }
  return { verts, faces };
}

/** Outward-oriented boundary planes (winding-agnostic: Newell + centroid). */
export function solidPlanes(s: ConvexSolid): SolidPlane[] {
  const c: Vec3 = [0, 0, 0];
  for (const v of s.verts) { c[0] += v[0]; c[1] += v[1]; c[2] += v[2]; }
  c[0] /= s.verts.length; c[1] /= s.verts.length; c[2] /= s.verts.length;
  const planes: SolidPlane[] = [];
  for (const f of s.faces) {
    let nx = 0, ny = 0, nz = 0;
    for (let i = 0; i < f.length; i++) {
      const a = s.verts[f[i]], b = s.verts[f[(i + 1) % f.length]];
      nx += (a[1] - b[1]) * (a[2] + b[2]);
      ny += (a[2] - b[2]) * (a[0] + b[0]);
      nz += (a[0] - b[0]) * (a[1] + b[1]);
    }
    const n = v3norm([nx, ny, nz]);
    if (!n) continue;
    let d = v3dot(n, s.verts[f[0]]);
    if (v3dot(n, c) > d) { n[0] = -n[0]; n[1] = -n[1]; n[2] = -n[2]; d = -d; }
    planes.push({ n, d });
  }
  return planes;
}

function solidEdgeDirs(s: ConvexSolid): Vec3[] {
  const dirs: Vec3[] = [];
  for (const f of s.faces) {
    for (let i = 0; i < f.length; i++) {
      const d = v3norm(v3sub(s.verts[f[(i + 1) % f.length]], s.verts[f[i]]));
      if (d && !dirs.some(e => Math.abs(v3dot(e, d)) > 0.9999)) dirs.push(d);
    }
  }
  return dirs;
}

/**
 * SAT between two convex solids. `minPen` (feet; default ≈1/42") means
 * face-to-face contact — a board LAID flush on a sheet — does NOT count as a
 * collision; only real interpenetration does.
 */
export function solidsCollide(A: ConvexSolid, B: ConvexSolid, minPen = 0.002): boolean {
  const axes: Vec3[] = [...solidPlanes(A).map(p => p.n), ...solidPlanes(B).map(p => p.n)];
  for (const a of solidEdgeDirs(A)) {
    for (const b of solidEdgeDirs(B)) {
      const x = v3norm(v3cross(a, b));
      if (x) axes.push(x);
    }
  }
  for (const ax of axes) {
    let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
    for (const v of A.verts) { const t = v3dot(ax, v); if (t < minA) minA = t; if (t > maxA) maxA = t; }
    for (const v of B.verts) { const t = v3dot(ax, v); if (t < minB) minB = t; if (t > maxB) maxB = t; }
    if (Math.min(maxA, maxB) - Math.max(minA, minB) < minPen) return false;
  }
  return true;
}

/** Sutherland–Hodgman in 3D: clip a face polygon to a set of half-spaces. */
export function clipPoly3D(poly: Vec3[], planes: SolidPlane[]): Vec3[] {
  let cur = poly;
  for (const { n, d } of planes) {
    if (cur.length === 0) break;
    const next: Vec3[] = [];
    for (let i = 0; i < cur.length; i++) {
      const a = cur[i], b = cur[(i + 1) % cur.length];
      const fa = v3dot(n, a) - d, fb = v3dot(n, b) - d;
      if (fa <= 1e-7) next.push(a);
      if ((fa < -1e-7 && fb > 1e-7) || (fa > 1e-7 && fb < -1e-7)) {
        const t = fa / (fa - fb);
        next.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1]), a[2] + t * (b[2] - a[2])]);
      }
    }
    cur = next;
  }
  return cur;
}

/**
 * Points spanning A∩B (both convex): every extreme point of the intersection
 * lies on a face of A clipped by B or vice versa. Enough to bound the overlap
 * along any axis / project its footprint. Empty when barely touching.
 */
export function solidIntersectionPoints(A: ConvexSolid, B: ConvexSolid): Vec3[] {
  const pA = solidPlanes(A), pB = solidPlanes(B);
  const out: Vec3[] = [];
  for (const f of A.faces) out.push(...clipPoly3D(f.map(i => A.verts[i]), pB));
  for (const f of B.faces) out.push(...clipPoly3D(f.map(i => B.verts[i]), pA));
  return out;
}

/** Merge overlapping/near-touching [a,b] intervals (units caller's; gap ≤ join). */
export function mergeIntervals(list: [number, number][], joinGap = 1 / 16): [number, number][] {
  const s = list.filter(iv => iv[1] > iv[0]).sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  for (const iv of s) {
    const last = out[out.length - 1];
    if (last && iv[0] <= last[1] + joinGap) last[1] = Math.max(last[1], iv[1]);
    else out.push([iv[0], iv[1]]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Wave 5 (CR #207): Autocut vs the TRUSS MODEL — ray-sampling helpers.
// The model is a triangle soup, so "inside the model" is recovered by casting
// rays and pairing surface crossings (entry/exit) by parity. Approximate by
// construction: it samples along a handful of lines, it is not an exact
// solid-vs-mesh intersection.

/**
 * Turn sorted-or-not ray-hit stations (units caller's, along one line) into
 * inside-intervals by parity pairing. Groups of coincident stations (within
 * `tol`) collapse first: an exit and an entry at the same spot — a ply joint
 * or two touching members — cancel each other (even-sized group), so the run
 * stays "inside" straight through; odd-sized groups keep one crossing.
 * Intervals are clamped to [0, maxIn]; those under `minCut` are dropped and
 * bounds land on the 1/16" grid.
 */
export function pairStationsToIntervals(stations: number[], maxIn: number, tol = 1 / 32, minCut = 0.125): [number, number][] {
  const s = [...stations].sort((a, b) => a - b);
  const ds: number[] = [];
  let i = 0;
  while (i < s.length) {
    let j = i + 1;
    while (j < s.length && s[j] - s[j - 1] <= tol) j++;
    if ((j - i) % 2 === 1) ds.push(s[Math.floor((i + j) / 2)]);
    i = j;
  }
  const out: [number, number][] = [];
  for (let k = 0; k + 1 < ds.length; k += 2) {
    const lo = Math.max(0, ds[k]);
    const hi = Math.min(maxIn, ds[k + 1]);
    if (hi - lo >= minCut) out.push([Math.round(lo * 16) / 16, Math.round(hi * 16) / 16]);
  }
  return out;
}

export interface CellBounds { i0: number; j0: number; i1: number; j1: number }

/**
 * 8-connected components of a covered-cell mask (row-major, nu × nv);
 * returns each component's cell-index bounds. Feeds the sheet Autocut:
 * each component becomes one bounding-rect cut-out.
 */
export function maskComponents(mask: boolean[], nu: number, nv: number): CellBounds[] {
  const seen = new Uint8Array(nu * nv);
  const out: CellBounds[] = [];
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || seen[start]) continue;
    seen[start] = 1;
    const stack = [start];
    const b: CellBounds = { i0: nu, j0: nv, i1: -1, j1: -1 };
    while (stack.length) {
      const c = stack.pop()!;
      const ci = c % nu, cj = (c - ci) / nu;
      if (ci < b.i0) b.i0 = ci;
      if (ci > b.i1) b.i1 = ci;
      if (cj < b.j0) b.j0 = cj;
      if (cj > b.j1) b.j1 = cj;
      for (let dj = -1; dj <= 1; dj++) {
        for (let di = -1; di <= 1; di++) {
          if (di === 0 && dj === 0) continue;
          const ni = ci + di, nj = cj + dj;
          if (ni < 0 || nj < 0 || ni >= nu || nj >= nv) continue;
          const idx = nj * nu + ni;
          if (mask[idx] && !seen[idx]) { seen[idx] = 1; stack.push(idx); }
        }
      }
    }
    out.push(b);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Wave 7 (CR #207): "Generate" — roof-plane region machinery. A tapped roof
// plane is recovered by rasterizing every coplanar model triangle onto a 1"
// grid in plane space. Truss models expose the roof plane only as the tops of
// the top chords — 1-1/2" stripes at 16"–24" o.c. — so the mask is closed
// morphologically (dilate+erode, square element) to bridge the framing gaps
// into one solid region the generators can work from.

/**
 * Binary closing (dilation then erosion by a (2r+1)² square element) on a
 * row-major nu × nv mask. Separable two-sweep passes, O(nu·nv) regardless of
 * r. Cells outside the array are treated as empty, so keep ≥ r cells of empty
 * padding around real content or the erosion will eat the borders.
 */
export function closeMask(mask: Uint8Array, nu: number, nv: number, r: number): Uint8Array {
  if (r <= 0) return mask.slice();
  const BIG = nu + nv + 4;
  const dil1 = (src: Uint8Array, horiz: boolean): Uint8Array => {
    const out = new Uint8Array(nu * nv);
    const n1 = horiz ? nv : nu, n2 = horiz ? nu : nv;
    for (let a = 0; a < n1; a++) {
      let d = BIG;
      for (let b = 0; b < n2; b++) {
        const idx = horiz ? a * nu + b : b * nu + a;
        d = src[idx] ? 0 : d + 1;
        if (d <= r) out[idx] = 1;
      }
      d = BIG;
      for (let b = n2 - 1; b >= 0; b--) {
        const idx = horiz ? a * nu + b : b * nu + a;
        d = src[idx] ? 0 : d + 1;
        if (d <= r) out[idx] = 1;
      }
    }
    return out;
  };
  const ero1 = (src: Uint8Array, horiz: boolean): Uint8Array => {
    const out = new Uint8Array(nu * nv);
    const n1 = horiz ? nv : nu, n2 = horiz ? nu : nv;
    const runL = new Int32Array(n2);
    for (let a = 0; a < n1; a++) {
      let run = 0; // consecutive filled cells ending here (border = empty)
      for (let b = 0; b < n2; b++) {
        const idx = horiz ? a * nu + b : b * nu + a;
        run = src[idx] ? run + 1 : 0;
        runL[b] = run;
      }
      let runR = 0;
      for (let b = n2 - 1; b >= 0; b--) {
        const idx = horiz ? a * nu + b : b * nu + a;
        runR = src[idx] ? runR + 1 : 0;
        if (runL[b] > r && runR > r) out[idx] = 1;
      }
    }
    return out;
  };
  return ero1(ero1(dil1(dil1(mask, true), false), true), false);
}

/**
 * The 8-connected component of `mask` containing cell index `start`, as its
 * own mask; null when the start cell is empty.
 */
export function componentAt(mask: Uint8Array, nu: number, nv: number, start: number): Uint8Array | null {
  if (start < 0 || start >= nu * nv || !mask[start]) return null;
  const out = new Uint8Array(nu * nv);
  out[start] = 1;
  const stack = [start];
  while (stack.length) {
    const c = stack.pop()!;
    const ci = c % nu, cj = (c - ci) / nu;
    for (let dj = -1; dj <= 1; dj++) {
      for (let di = -1; di <= 1; di++) {
        if (di === 0 && dj === 0) continue;
        const ni = ci + di, nj = cj + dj;
        if (ni < 0 || nj < 0 || ni >= nu || nj >= nv) continue;
        const idx = nj * nu + ni;
        if (mask[idx] && !out[idx]) { out[idx] = 1; stack.push(idx); }
      }
    }
  }
  return out;
}

/**
 * Contour-trace a uniform-grid mask into outline rings (coordinates in cell
 * units, i.e. inches on the 1" generator grid). Same tracer as sheetOutlines:
 * boundary segments keep the interior on the left, outer rings come out CCW,
 * hole rings CW, degenerate diagonal-touch corners prefer the sharpest right
 * turn, and collinear midpoints are dropped.
 */
export function maskOutlines(mask: Uint8Array, nu: number, nv: number): SheetOutline[] {
  const at = (i: number, j: number) => i >= 0 && j >= 0 && i < nu && j < nv && mask[j * nu + i] === 1;
  type Seg = { a: Pt; b: Pt; used: boolean };
  const segs: Seg[] = [];
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      if (!at(i, j)) continue;
      if (!at(i, j - 1)) segs.push({ a: [i, j], b: [i + 1, j], used: false });         // bottom → +x
      if (!at(i, j + 1)) segs.push({ a: [i + 1, j + 1], b: [i, j + 1], used: false }); // top → −x
      if (!at(i - 1, j)) segs.push({ a: [i, j + 1], b: [i, j], used: false });         // left → −y
      if (!at(i + 1, j)) segs.push({ a: [i + 1, j], b: [i + 1, j + 1], used: false }); // right → +y
    }
  }
  const key = (p: Pt) => p[1] * (nu + 2) + p[0];
  const byStart = new Map<number, Seg[]>();
  for (const s of segs) {
    const k = key(s.a);
    const list = byStart.get(k);
    if (list) list.push(s); else byStart.set(k, [s]);
  }
  const loops: Pt[][] = [];
  for (const startSeg of segs) {
    if (startSeg.used) continue;
    const loop: Pt[] = [];
    let cur = startSeg;
    for (let guard = 0; guard < segs.length + 1; guard++) {
      cur.used = true;
      loop.push(cur.a);
      const nexts = (byStart.get(key(cur.b)) ?? []).filter(s => !s.used);
      if (nexts.length === 0) break;
      const dir: Pt = [Math.sign(cur.b[0] - cur.a[0]), Math.sign(cur.b[1] - cur.a[1])];
      const right: Pt = [dir[1], -dir[0]];
      nexts.sort((s1, s2) => {
        const d1: Pt = [Math.sign(s1.b[0] - s1.a[0]), Math.sign(s1.b[1] - s1.a[1])];
        const d2: Pt = [Math.sign(s2.b[0] - s2.a[0]), Math.sign(s2.b[1] - s2.a[1])];
        const score = (d: Pt) => d[0] * right[0] + d[1] * right[1] - (d[0] * dir[0] + d[1] * dir[1]) * 0.5;
        return score(d2) - score(d1);
      });
      cur = nexts[0];
      if (key(cur.a) === key(startSeg.a)) break;
    }
    const clean: Pt[] = [];
    for (let i = 0; i < loop.length; i++) {
      const p0 = loop[(i + loop.length - 1) % loop.length], p1 = loop[i], p2 = loop[(i + 1) % loop.length];
      const collinear = (p1[0] - p0[0]) * (p2[1] - p0[1]) - (p1[1] - p0[1]) * (p2[0] - p0[0]);
      if (Math.abs(collinear) > 1e-6) clean.push(p1);
    }
    if (clean.length >= 3) loops.push(clean);
  }
  const outers = loops.filter(l => polySignedArea(l) > 0);
  const holes = loops.filter(l => polySignedArea(l) < 0);
  const result: SheetOutline[] = outers.map(outer => ({ outer, holes: [] }));
  for (const h of holes) {
    const cx = h.reduce((s, p) => s + p[0], 0) / h.length;
    const cy = h.reduce((s, p) => s + p[1], 0) / h.length;
    const owner = result.find(r2 => pointInPoly([cx, cy], r2.outer)) ?? result[0];
    if (owner) owner.holes.push(h);
  }
  return result;
}

/** Complement of merged intervals within [0, L]; spans under minKeep dropped. */
export function complementSpans(merged: [number, number][], L: number, minKeep = 0.25): [number, number][] {
  const out: [number, number][] = [];
  let cursor = 0;
  for (const [a, b] of merged) {
    if (a - cursor >= minKeep) out.push([cursor, a]);
    cursor = Math.max(cursor, b);
  }
  if (L - cursor >= minKeep) out.push([cursor, L]);
  return out;
}
